/**
 * SSML Readme のエントリ一覧 (選択中カテゴリの本文)。
 *
 * 1 エントリにつき **SSML 構文 / 説明 / 利用例 / 期待効果 / サンプル** を表示する
 * (`docs/ssml-tuning-plan.md` 8.4.3 / MVP 項目 14)。
 *
 * ## 設計上の約束
 *
 * - **データは props で受ける** (`categories.ts` を import しない)。
 *   Phase2 で `GET /ssml-readme` に切り替わっても本コンポーネントは無変更で使える (R14)。
 * - MVP の Readme は **読むだけ**。タグ挿入支援などの編集機能は Phase2 送りのため、
 *   ここには操作要素を置かない。ただしコピーしやすいよう構文とサンプルは
 *   `<code>` / `<pre>` で表示する (計画 4.2)。
 */
import type React from 'react';

import type { SsmlReadmeCategory } from './types';

export interface ReadmeEntryListProps {
  /** 表示するカテゴリ (選択中の 1 件) */
  category: SsmlReadmeCategory;
}

export function ReadmeEntryList({ category }: ReadmeEntryListProps): React.JSX.Element {
  return (
    <section
      className="ssml-readme-panel"
      role="tabpanel"
      id={`ssml-readme-panel-${category.category}`}
      aria-labelledby={`ssml-readme-tab-${category.category}`}
      data-testid={`ssml-readme-panel-${category.category}`}
    >
      <h3 className="ssml-readme-panel__title">{category.label}</h3>
      <p className="ssml-readme-panel__description">{category.description}</p>

      {/*
        エントリが 0 件のカテゴリは MVP には存在しないが、
        Phase2 で API 化した際に空応答で画面が崩れないよう分岐は用意しておく。
      */}
      {category.entries.length === 0 ? (
        <p className="ssml-readme-panel__empty" data-testid="ssml-readme-empty">
          このカテゴリにはまだ構文が登録されていません。
        </p>
      ) : (
        <ul className="ssml-readme-entries">
          {category.entries.map((entry) => (
            <li
              key={entry.syntax + entry.title}
              className="ssml-readme-entry"
              data-testid="ssml-readme-entry"
            >
              <h4 className="ssml-readme-entry__title">{entry.title}</h4>

              <dl className="ssml-readme-entry__fields">
                <dt>SSML 構文</dt>
                <dd>
                  <code className="ssml-readme-entry__syntax">{entry.syntax}</code>
                </dd>

                <dt>説明</dt>
                <dd>{entry.description}</dd>

                <dt>利用例</dt>
                <dd>
                  <ul className="ssml-readme-entry__usages">
                    {entry.usages.map((usage) => (
                      <li key={usage}>{usage}</li>
                    ))}
                  </ul>
                </dd>

                <dt>期待効果</dt>
                <dd>{entry.expectedEffect}</dd>

                <dt>サンプル</dt>
                <dd>
                  {/* コピーして SSML 編集欄へ貼れるよう、改行と空白を保持して表示する */}
                  <pre className="ssml-readme-entry__sample">{entry.sampleSsml}</pre>
                </dd>
              </dl>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
